import React, { useEffect, useState } from 'react'
import { getThemes, getUserContexts } from '../../reducers/portalConfig';
import { connect, useDispatch, useSelector } from 'react-redux';
import Branding from '../../components/GlobalHeader/Branding';
import { Box, Button, makeStyles, Typography } from '@material-ui/core';
import { getAlternateLogo } from '../../utils';
import styles from "./styles";
import { getI18n } from '../../utils/intl';
import { useIntl } from 'react-intl';
import { JSEncrypt } from 'jsencrypt';
import { AlertMessages, FormDialog, FormTextField, Loading } from '../../components';
import { getPublicKey, getRegexError, getRegexPolicy } from "../../reducers/userProfile"
import { ACTIONS, changePasswordReducer } from './reducer';
import { debounce } from 'lodash';
import { useLocation } from 'react-router';
import { fetchChangePasswordTokenValid, updateChangePassword } from '../../actions/changePassword';
import { getIsLoading, getUpdatedChangePasswordErrors, getUpdatedChangePasswordSuccess, getValidateChangePasswordTokenSuccess } from '../../reducers/changePassword';
import { initializeChangePasswordState, validateFormInputs } from './utils';
import PasswordPolicy from '../../pageComponents/PasswordPolicy';
import { fetchPasswordPolicy, fetchPublicKey } from '../../actions/userProfile';
import { compose } from 'recompose';
import PropTypes from "prop-types";
import { ROUTES } from '../../constants/routes';
import { terminateAllSessions } from '../../actions/newPassword';

const labels = {
  oldPassword: "label.change.password.page.old.password.text",
  password: "label.user.account.setup.password.label",
  confirmPassword: "label.user.account.setup.password.confirm.label",
}

const ChangePassword = (props) => {
  const {
    fetchPasswordPolicy,
    fetchPublicKey,
    publicKey,
  } = props

  const [isUserContextExists, setIsUserContextExists] = useState(false);
  const [alertMessages, setAlertMessages] = useState({
    status: false,
    message: "",
    variant: "",
  })

  const [state, dispatch] = React.useReducer(
    changePasswordReducer, null, initializeChangePasswordState,
  );

  const useStyles = makeStyles(styles);
  const classes = useStyles();
  const intl = getI18n(useIntl());
  const globalDispatch = useDispatch();

  const { logo, siteTitle } = useSelector(getThemes);
  const regexPolicy = useSelector(getRegexPolicy);
  const regexError = useSelector(getRegexError);
  const alternateLogo = getAlternateLogo(siteTitle);
  const isLoading = useSelector(getIsLoading);
  const isChangePasswordUpdationSuccess = useSelector(getUpdatedChangePasswordSuccess);
  const changePasswordUpdationErrors = useSelector(getUpdatedChangePasswordErrors);
  const isTokenValid = useSelector(getValidateChangePasswordTokenSuccess);
  const userContext = useSelector(getUserContexts)

  const validateFieldValue = debounce(({ field, dispatch, intl, regexPolicy }) => {
    dispatch({ type: ACTIONS.UPDATE_FIELD_ERRORS, payload: { field, intl, regexPolicy } })
  }, 300);

  const handleChange = (field, value) => {
    dispatch({ type: ACTIONS.UPDATE_FIELD_VALUE, payload: { field, value } });
    validateFieldValue({ field, dispatch, intl, regexPolicy });
  }

  const { hash } = useLocation();
  const hashArray = hash.split("/");

  const token = hashArray.length >= 2 && hashArray[0].includes("token") && hashArray[1];

  useEffect(() => {
    fetchPublicKey();
  }, []);

  useEffect(() => {
    if (token) {
      globalDispatch(fetchChangePasswordTokenValid(token));
    }
  }, [token]);

  useEffect(() => {
    if (!userContext && (!token || isTokenValid === false)) {
      window.location.href = ROUTES.invalidToken.path;
    }
  }, [token, isTokenValid, userContext]);

  useEffect(() => {
    if (userContext) {
      setIsUserContextExists(true);
    } else {
      setIsUserContextExists(false);
    }
  }, [userContext])

  const handleSubmit = (e) => {
    e.preventDefault();
    const { validatedState, isFormValid } = validateFormInputs({ state, intl, regexPolicy });
    if (isFormValid) {
      if (token) {
        const payload = {
          uuid: token,
          newPassword: state.confirmPassword.value,
          password: state.oldPassword.value,
        }
        if (publicKey) {
          const encrypt = new JSEncrypt();
          encrypt.setPublicKey(publicKey);
          payload.password = encrypt.encrypt(payload.password);
          payload.newPassword = encrypt.encrypt(payload.newPassword);
        }
        globalDispatch(updateChangePassword(payload, publicKey));
      }
    } else {
      dispatch({ type: ACTIONS.UPDATE_FORM_ERRORS, payload: validatedState });
    }
  }

  useEffect(() => {
    if (isChangePasswordUpdationSuccess) {
      setAlertMessages({
        status: true,
        message: intl.getI18nMessage("label.new.password.success.message"),
        variant: "success",
      });
      window.location.href = '/admin/app/home';
    } else if (changePasswordUpdationErrors) {
      setAlertMessages({
        status: true,
        message: changePasswordUpdationErrors,
        variant: "error",
      });
    } else if (regexError) {
      setAlertMessages({
        status: true,
        message: intl.getI18nMessage("label.change.password.page.load.error"),
        variant: "error",
      });
    }
  }, [changePasswordUpdationErrors, isChangePasswordUpdationSuccess, regexError]);

  return (
    <>
      {
        alertMessages?.status &&
        <AlertMessages
          className={classes.alertMessagesContainer}
          variant={alertMessages.variant}
          message={alertMessages.message}
          onClose={() => setAlertMessages({ ...alertMessages, status: false })}
        />
      }
      <FormDialog
        isOpen={isUserContextExists}
        hideCancelButton
        title={intl.getI18nMessage("label.new.password.session.detect.message")}
        submitText={intl.getI18nMessage("label.new.password.session.detect.terminate.button.text")}
        paperClass={classes.formDialog}
        onSubmit={() => terminateAllSessions()}
      >
        <Typography>{intl.getI18nMessage("label.new.password.session.detect.description.message")}</Typography>
        <Typography className={classes.formDialogDescription}>{intl.getI18nMessage("label.new.password.session.detect.description.help.text")}</Typography>
      </FormDialog>
      <Box className={classes.pageContentContainer}>
        <div className={classes.pageContent}>
          {
            isLoading &&
            <Loading pageLoader />
          }

          <Box className={`${classes.flexCenter} ${classes.imgLogo}`}>
            {logo ? <Branding logo={logo} /> : <Typography variant='h1' className={classes.alternateLogo}>
              {alternateLogo}
            </Typography>}
          </Box>
          <Typography className={classes.heading} variant='h1'>{intl.getI18nMessage("label.change.password.button.text")}</Typography>
          <Typography className={classes.pageDescription} variant='h4'>{intl.getI18nMessage("label.change.password.description")}</Typography>
          <form onSubmit={(e) => handleSubmit(e)}>
            <FormTextField
              id='change-password-page-old-password-field'
              type='password'
              name={intl.getI18nMessage(labels.oldPassword)}
              fieldContainerClass={classes.fieldContainer}
              helperText={state.oldPassword.errors.length > 0 ? state.oldPassword.errors[0] : null}
              errorHelperText={state.oldPassword.errors.length > 0}
              error={state.oldPassword.errors.length > 0}
              value={state.oldPassword.value}
              handleChange={value => handleChange('oldPassword', value)}
            />

            <FormTextField
              id='change-password-page-password-field'
              type='password'
              name={intl.getI18nMessage(labels.password)}
              fieldContainerClass={classes.fieldContainer}
              helperText={state.password.errors.length > 0 ? state.password.errors[0] : null}
              errorHelperText={state.password.errors.length > 0}
              error={state.password.errors.length > 0}
              value={state.password.value}
              handleChange={value => handleChange('password', value)}
            />

            <PasswordPolicy
              fetchPasswordPolicy={fetchPasswordPolicy}
              regexPolicy={regexPolicy}
            />

            <FormTextField
              id='change-password-page-confirm-password-field'
              type='password'
              name={intl.getI18nMessage(labels.confirmPassword)}
              fieldContainerClass={classes.fieldContainer}
              helperText={
                state.confirmPassword.errors.length > 0
                  ? state.confirmPassword.errors[0]
                  : null
              }
              errorHelperText={state.confirmPassword.errors.length > 0}
              error={state.confirmPassword.errors.length > 0}
              value={state.confirmPassword.value}
              handleChange={value => handleChange('confirmPassword', value)}
            />
            <Button
              className={classes.submitButton}
              id='change-password-submit-button'
              data-apim-test='change-password-submit-button'
              variant='contained'
              color='secondary'
              type='submit'
              disabled={Boolean(regexError)}
            >
              {intl.getI18nMessage("label.change.password.button.text")}
            </Button>
          </form>
        </div>
      </Box>
    </>
  )
}

const mapStateToProps = (state) => ({
  publicKey: getPublicKey(state),
});

const mapDispatchToProps = {
  fetchPasswordPolicy,
  fetchPublicKey,
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
)(ChangePassword);

ChangePassword.propTypes = {
  fetchPasswordPolicy: PropTypes.func,
  fetchPublicKey: PropTypes.func,
  publicKey: PropTypes.string,
}

